import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../../shared/services/auth.service';
import { CryptoService } from '../../../shared/services/crypto.service';
import { ProjectV2ServiceService } from '../../../shared/services/project-v2-service.service';
import { TimelineService } from '../../../shared/services/timeline.service';
import { Subject, takeUntil } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MilestoneStatusEditorComponent } from '../milestone-status-editor/milestone-status-editor.component';
import { CampaignMilestone, TimelineDisplayMode } from '../../../shared/interfaces';
import { TIMELINE_BLOCKS_CONSTANTS } from './models/timeline-block-constants';

@Component({
  selector: 'mtm-timeline-blocks',
  templateUrl: './timeline-blocks.component.html',
  styleUrls: ['./timeline-blocks.component.scss', '../timeline-style.scss'],
  standalone: false,
})
export class TimelineBlocksComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('pinnedTimelineDiv', { static: false }) pinnedTimelineDiv: ElementRef;
  @Output() dataEvent: EventEmitter<any> = new EventEmitter<any>();
  @Input() isDriveCampaign = false;
  @Input() timeLineData: any = [];
  @Input() editTimelineComponent: boolean = false;
  @Input() isRecap: boolean = false;
  @Input() isHallmark: boolean = false;
  @Input() IsDownload: boolean = false;
  @Input() IsSummary: boolean = false;
  @Input() multiViewsSupport: boolean = false;
  @Input() canEdit: boolean = true;

  private readonly timelinesSignal = signal<any[]>([]);
  private readonly pinnedTimelinesSignal = signal<any[]>([]);
  private readonly pinnedTimelineDivWidthSignal = signal<number>(0);
  private readonly showTooltipSignal = signal<boolean>(false);
  private readonly selectedMilestoneSignal = signal<any>(null);
  private readonly maxWidthSignal = signal<number>(0);
  private readonly colorInfoDataSignal = signal<any[]>([]);
  private readonly isHiddenTimelineSignal = signal<boolean>(true);
  private readonly isShowSignal = signal<boolean>(false);
  private readonly timelineElementWidthSignal = signal<number>(0);
  private readonly fixedTimelineSizeSignal = signal<boolean>(false);
  private readonly isLeftArrowVisibleSignal = signal<boolean>(false);
  private readonly isRightArrowVisibleSignal = signal<boolean>(false);
  private readonly isRightArrowVisibleForFiveSignal = signal<boolean>(false);
  private readonly isLeftArrowVisibleForFiveSignal = signal<boolean>(false);
  private readonly displayModeSignal = signal<TimelineDisplayMode>(TimelineDisplayMode.Horizontal);

  get timelines(): any[] {
    return this.timelinesSignal();
  }
  set timelines(value: any[]) {
    this.timelinesSignal.set(value ?? []);
  }

  get pinnedTimelines(): any[] {
    return this.pinnedTimelinesSignal();
  }
  set pinnedTimelines(value: any[]) {
    this.pinnedTimelinesSignal.set(value ?? []);
  }

  get pinnedTimelineDivWidth(): number {
    return this.pinnedTimelineDivWidthSignal();
  }
  set pinnedTimelineDivWidth(value: number) {
    this.pinnedTimelineDivWidthSignal.set(value ?? 0);
  }

  get showTooltip(): boolean {
    return this.showTooltipSignal();
  }
  set showTooltip(value: boolean) {
    this.showTooltipSignal.set(!!value);
  }

  get selectedMilestone(): any {
    return this.selectedMilestoneSignal();
  }
  set selectedMilestone(value: any) {
    this.selectedMilestoneSignal.set(value ?? null);
  }

  get maxWidth(): number {
    return this.maxWidthSignal();
  }
  set maxWidth(value: number) {
    this.maxWidthSignal.set(value ?? 0);
  }

  get colorInfoData(): any[] {
    return this.colorInfoDataSignal();
  }
  set colorInfoData(value: any[]) {
    this.colorInfoDataSignal.set(value ?? []);
  }

  get isHiddenTimeline(): boolean {
    return this.isHiddenTimelineSignal();
  }
  set isHiddenTimeline(value: boolean) {
    this.isHiddenTimelineSignal.set(!!value);
  }

  get isShow(): boolean {
    return this.isShowSignal();
  }
  set isShow(value: boolean) {
    this.isShowSignal.set(!!value);
  }

  get timelineElementWidth(): number {
    return this.timelineElementWidthSignal();
  }
  set timelineElementWidth(value: number) {
    this.timelineElementWidthSignal.set(value ?? 0);
  }

  get fixedTimelineSize(): boolean {
    return this.fixedTimelineSizeSignal();
  }
  set fixedTimelineSize(value: boolean) {
    this.fixedTimelineSizeSignal.set(!!value);
  }

  get isLeftArrowVisible(): boolean {
    return this.isLeftArrowVisibleSignal();
  }
  set isLeftArrowVisible(value: boolean) {
    this.isLeftArrowVisibleSignal.set(!!value);
  }

  get isRightArrowVisible(): boolean {
    return this.isRightArrowVisibleSignal();
  }
  set isRightArrowVisible(value: boolean) {
    this.isRightArrowVisibleSignal.set(!!value);
  }

  get isRightArrowVisibleForFive(): boolean {
    return this.isRightArrowVisibleForFiveSignal();
  }
  set isRightArrowVisibleForFive(value: boolean) {
    this.isRightArrowVisibleForFiveSignal.set(!!value);
  }

  get isLeftArrowVisibleForFive(): boolean {
    return this.isLeftArrowVisibleForFiveSignal();
  }
  set isLeftArrowVisibleForFive(value: boolean) {
    this.isLeftArrowVisibleForFiveSignal.set(!!value);
  }

  get displayMode(): TimelineDisplayMode {
    return this.displayModeSignal();
  }
  set displayMode(value: TimelineDisplayMode) {
    this.displayModeSignal.set(value ?? TimelineDisplayMode.Horizontal);
  }

  widthPerClosedBlock = TIMELINE_BLOCKS_CONSTANTS.WIDTH.PER_CLOSED_BLOCK;
  widthPerMilestone = TIMELINE_BLOCKS_CONSTANTS.WIDTH.PER_MILESTONE;
  marginPerBlock = TIMELINE_BLOCKS_CONSTANTS.WIDTH.MARGIN_PER_BLOCK;
  timelineDropdown: any = [...TIMELINE_BLOCKS_CONSTANTS.DEFAULTS.DROPDOWN];
  colors = TIMELINE_BLOCKS_CONSTANTS.COLORS;
  dcTimelineMilestone: any = {};
  authUser: any;
  ngUnsubscribe = new Subject();
  workspaceId: string;
  canViewEditTimeline: boolean = true;
  protected readonly TimelineDisplayMode = TimelineDisplayMode;

  gateMilestoneNames = [...TIMELINE_BLOCKS_CONSTANTS.GATE_MILESTONE_NAMES];

  constructor(
    private readonly router: Router,
    private readonly projectV2ServiceService: ProjectV2ServiceService,
    private readonly authUserService: AuthService,
    private readonly timelineService: TimelineService,
    private readonly cryptoService: CryptoService,
    private readonly route: ActivatedRoute,
    private readonly modalService: NgbModal
  ) {
    this.authUser = this.authUserService.getAuthUser();
    router.events.subscribe(() => {
      this.resetOpacity();
    });
  }

  ngOnInit(): void {
    this.calculateMaxWidth();
    this.decryptIds();
    this.handleTimeline().then(() => {});
    this.timelineService.showCrossBtn.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (data) => {
        this.isShow = data;
      },
      error: (err) => console.error(err),
    });
    this.timelineService.timelineDisplay$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (mode) => {
        this.displayMode = mode;
      },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.timeLineData) {
      const updatedData = changes.timeLineData.currentValue;
      this.handleTimelineDataUpdate(updatedData);
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  @HostListener('window:resize')
  onResize() {
    this.calculateMaxWidth();
  }

  private calculateMaxWidth() {
    const screenWidth = window.innerWidth;
    const a = screenWidth - 200;
    let maxWidth = a - (a % TIMELINE_BLOCKS_CONSTANTS.WIDTH.PER_MILESTONE);
    if (maxWidth < TIMELINE_BLOCKS_CONSTANTS.WIDTH.MIN_MAX_WIDTH) {
      maxWidth = TIMELINE_BLOCKS_CONSTANTS.WIDTH.MIN_MAX_WIDTH;
    }
    this.maxWidth = maxWidth;
  }

  getFormattedDate(dateString: string): any {
    const date = new Date(dateString);
    return { day: date.getDate(), month: date.getMonth() + 1, year: date.getFullYear() };
  }

  decryptIds() {
    this.route.queryParams.subscribe((query) => {
      if (query?.wid && query.dc) {
        this.workspaceId = this.cryptoService.decryptUsingAES256(query.wid);
      }
    });
  }

  handleTimelineDataUpdate(updatedData: any) {
    this.timeLineData = updatedData;
    this.handleTimeline().then(() => {});
  }

  async handleTimeline() {
    this.timelines = await this.projectV2ServiceService.handleTimelineData(this.timeLineData, this.isHallmark);
    this.timelineService.selectedMilestone$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((result) => {
      if (result?.name) {
        this.selectedMilestone = result;
        this.updateMilestone(result);
      }
    });

    if (this.timelines) {
      let timelineTotal = TIMELINE_BLOCKS_CONSTANTS.DEFAULTS.INITIAL_TIMELINE_TOTAL;
      this.fixedTimelineSize = false;
      this.timelineElementWidth = 0;

      this.timelines.forEach((elem: any) => {
        if (elem?.items?.length > 0) {
          timelineTotal += elem.items.length;
        }
      });

      if (timelineTotal) {
        if (
          timelineTotal * TIMELINE_BLOCKS_CONSTANTS.WIDTH.PER_MILESTONE <=
          TIMELINE_BLOCKS_CONSTANTS.WIDTH.DEFAULT_CONTAINER_WIDTH
        ) {
          this.timelineElementWidth = TIMELINE_BLOCKS_CONSTANTS.WIDTH.DEFAULT_CONTAINER_WIDTH / timelineTotal;
          this.fixedTimelineSize = true;
        }
      }
    }

    setTimeout(() => {
      this.checkScrollOffset();
    }, 100);

    for (let i = 0; i < 3 && this.timelines.length - 1; ++i) {
      const temp = this.timelines[i];
      this.timelines[i] = this.timelines[i + 1];
      this.timelines[i + 1] = temp;
    }
  }

  toggleBlockView(timeline: any) {
    const blockIndex = this.timelines.indexOf(timeline);
    this.timelines[blockIndex].showBlock = !this.timelines[blockIndex].showBlock;
  }

  checkScrollState(event: Event) {
    event.stopPropagation();
    this.checkScrollOffset();
  }

  checkScrollOffset() {
    const target = document.querySelector('.timeline-scroll') as HTMLElement;
    if (!target) return;

    this.isLeftArrowVisible = target.scrollLeft > 0;
    this.isRightArrowVisible = target.scrollWidth - target.scrollLeft - target.clientWidth - 1 > 0;
    this.isRightArrowVisibleForFive = target.scrollWidth - target.scrollLeft - target.clientWidth > 425;
    this.isLeftArrowVisibleForFive = target.scrollLeft > 425;
  }

  scrollToLeft(slide: number = TIMELINE_BLOCKS_CONSTANTS.WIDTH.SCROLL_SLIDE) {
    const scrollContainer = document.getElementById('scroll-container');
    if (scrollContainer) {
      scrollContainer.scrollBy({ left: -slide, behavior: 'smooth' });
    }
  }

  scrollToRight(slide: number = TIMELINE_BLOCKS_CONSTANTS.WIDTH.SCROLL_SLIDE) {
    const scrollContainer = document.getElementById('scroll-container');
    if (scrollContainer) {
      scrollContainer.scrollBy({ left: +slide, behavior: 'smooth' });
    }
  }

  handlePinBlocks(block: any) {
    if (block) {
      const index = this.timelines.indexOf(block);
      this.timelines[index].pinBlock = !this.timelines[index].pinBlock;

      if (this.timelines[index].pinBlock) {
        this.pinnedTimelines.push(block);
      } else {
        this.pinnedTimelines = this.pinnedTimelines.filter((el: any) => el.name !== block.name);
      }
    }

    let milestoneCount = 0;
    let closedBlockCount = 0;
    this.pinnedTimelines.forEach((elem: any) => {
      if (elem.items?.length && elem.showBlock) {
        elem.items.forEach((subItem: any) => {
          if (subItem.items?.length) {
            milestoneCount += subItem.items.length;
          } else {
            milestoneCount += 1;
          }
        });
      } else {
        closedBlockCount += 1;
      }
    });

    this.pinnedTimelineDivWidth =
      this.widthPerMilestone * milestoneCount +
      (this.pinnedTimelines.length - closedBlockCount) * this.marginPerBlock +
      closedBlockCount * this.widthPerClosedBlock;
  }

  get pinnedTimelineDivWidthSize(): number {
    return this.pinnedTimelines.length > 0 ? this.pinnedTimelineDiv?.nativeElement?.offsetWidth : 0;
  }

  updateMilestone(data: any) {
    this.timelineService.lastSelectedMilestone = data;

    this.resetAllMilestones();

    if (this.shouldHighlightSingleMilestone(data)) {
      setTimeout(() => {
        this.highlightMilestone(data);
      }, 100);
    } else {
      this.highlightAllMilestones();
    }
  }

  private resetAllMilestones() {
    this.timelines.forEach((timeline: any) => {
      timeline.items.forEach((milestone: any) => {
        this.resetMilestone(milestone);

        if (milestone.items) {
          milestone.items.forEach((subMilestone: any) => this.resetMilestone(subMilestone));
        }
      });
    });
  }

  private resetMilestone(milestone: any) {
    milestone.opacity = TIMELINE_BLOCKS_CONSTANTS.UI.OPACITY.DIM;
    milestone['isCross'] = false;
    milestone.statusColor = TIMELINE_BLOCKS_CONSTANTS.UI.STATUS_COLORS.DEFAULT;
  }

  private shouldHighlightSingleMilestone(data: any): boolean {
    return !!(data && !this.editTimelineComponent && this.selectedMilestone && !this.isRecap);
  }

  private highlightMilestone(data: any) {
    this.timelines.forEach((timeline: any) => {
      timeline.items.forEach((milestone: any) => {
        if (this.isMatchingMilestone(milestone, data)) {
          this.setMilestoneHighlight(milestone, data);
        }
      });
    });
  }

  private isMatchingMilestone(milestone: any, data: any): boolean {
    const hasMatchingSub = milestone.items?.some((sub: any) => sub.name === data.name && sub.id === data.id);
    const isDirectMatch = !milestone.items && milestone.name === data.name && milestone.mad === data.mad;

    return hasMatchingSub || isDirectMatch;
  }

  private setMilestoneHighlight(milestone: any, data: any) {
    milestone.opacity = TIMELINE_BLOCKS_CONSTANTS.UI.OPACITY.FULL;
    milestone['isCross'] = true;
    milestone.status = data.status;
    milestone.statusColor = this.dcTimelineMilestone.color;
  }

  private highlightAllMilestones() {
    this.timelines.forEach((timeline: any) => {
      timeline.items.forEach((milestone: any) => {
        milestone.opacity = TIMELINE_BLOCKS_CONSTANTS.UI.OPACITY.FULL;
        milestone['isCross'] = true;
      });
    });
  }

  resetOpacity() {
    const currentPath = window.location.pathname;
    if (!TIMELINE_BLOCKS_CONSTANTS.ROUTES.MATCH_PATHS.includes(currentPath)) {
      for (const item of this.timelines) {
        for (const subItem of item?.items ?? []) {
          subItem.opacity = TIMELINE_BLOCKS_CONSTANTS.UI.OPACITY.FULL;
        }
      }
    }
  }

  backToDashboard(milestone: any) {
    const dcId = milestone.driveCampaignId;
    this.timelineService.showCrossBtn.next(true);
    this.router
      .navigate(TIMELINE_BLOCKS_CONSTANTS.ROUTES.DASHBOARD, {
        queryParams: {
          wid: this.cryptoService.encryptString(this.workspaceId),
          dc: this.cryptoService.encryptString(dcId),
        },
      })
      .then(() => {});
  }

  updateTimelines(UpdatedTimelinesData: any) {
    this.timelines = UpdatedTimelinesData;
  }

  editMilestone(milestone: CampaignMilestone) {
    if (!this.canEdit) return;
    if (this.editTimelineComponent || this.isRecap || this.IsDownload) return;
    if (!!milestone.items && milestone.items.length > 0) return;

    const modalRef = this.modalService.open(MilestoneStatusEditorComponent, {
      size: TIMELINE_BLOCKS_CONSTANTS.UI.MODAL.SIZE,
      modalDialogClass: TIMELINE_BLOCKS_CONSTANTS.UI.MODAL.CLASS,
    });
    modalRef.componentInstance.milestone = milestone;
    modalRef.componentInstance.campaign = this.timeLineData;
    modalRef.result
      .then(() => {
        this.handleTimeline().then(() => {});
      })
      .catch(() => {});
  }

  @HostBinding('class')
  get classes(): Record<string, boolean> {
    return {
      'horizontal-timeline-parent':
        this.displayMode == TimelineDisplayMode.Horizontal || this.displayMode == TimelineDisplayMode.VerticalList,
      'horizontal-multiline-parent': this.displayMode == TimelineDisplayMode.HorizontalMultiLine,
      'vertical-timeline-parent': this.displayMode === TimelineDisplayMode.VerticalList,
    };
  }
}
